import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface TeamMember {
  name: string;
  role: string;
  specialty: string;
  image: string;
  alt: string;
  availability: string;
  languages: string[];
}

export default function SupportTeam() {
  const teamMembers: TeamMember[] = [
  {
    name: 'Dr. Sarah Mitchell',
    role: 'Lead Technical Specialist',
    specialty: 'CNC Machining & Materials Engineering',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e95358c6-1772081363912.png",
    alt: 'Professional woman with brown hair in navy blazer smiling confidently in modern aerospace manufacturing facility',
    availability: 'Mon-Fri, 8AM-6PM EST',
    languages: ['English', 'German']
  },
  {
    name: 'James Rodriguez',
    role: 'Compliance & Quality Manager',
    specialty: 'AS9100, ITAR, Regulatory Standards',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1146104c7-1763299435840.png",
    alt: 'Hispanic man in professional attire with short black hair reviewing quality documentation in aerospace office',
    availability: 'Mon-Fri, 9AM-7PM EST',
    languages: ['English', 'Spanish']
  },
  {
    name: 'Emily Chen',
    role: 'Platform Support Lead',
    specialty: 'Client Portal, Project Management Tools',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_120f980d1-1763300650373.png",
    alt: 'Asian woman with glasses and professional attire working on computer in modern tech support center',
    availability: '24/7 Support Available',
    languages: ['English', 'Mandarin']
  },
  {
    name: 'Michael Thompson',
    role: 'Manufacturing Engineer',
    specialty: 'Additive Manufacturing, Prototyping',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_165a48706-1783233117166.png",
    alt: 'Professional man with beard in industrial setting examining 3D printed aerospace component',
    availability: 'Mon-Fri, 7AM-5PM EST',
    languages: ['English', 'French']
  }];


  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-accent/10 border border-accent/20 rounded-full mb-6">
            <Icon name="UserGroupIcon" size={20} className="text-accent" />
            <span className="font-cta text-sm text-accent tracking-wider">EXPERT TEAM</span>
          </div>

          <h2 className="font-headline text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Meet Your
            <span className="block text-accent mt-2">Support Specialists</span>
          </h2>

          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Our team of aerospace manufacturing experts brings decades of combined experience in technical support, compliance, and project management
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {teamMembers.map((member, index) =>
          <div
            key={index}
            className="group bg-card border border-border rounded-xl overflow-hidden hover:border-accent transition-all duration-300 hover:shadow-glow">

              {/* Image */}
              <div className="relative h-64 overflow-hidden">
                <AppImage
                src={member.image}
                alt={member.alt}
                className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500" />

                <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/50 to-transparent opacity-60"></div>
                
                {/* Availability Badge */}
                <div className="absolute top-4 right-4 px-3 py-1 bg-success/90 backdrop-blur-sm rounded-full flex items-center space-x-1">
                  <div className="w-2 h-2 bg-success rounded-full animate-pulse-glow"></div>
                  <span className="text-xs font-cta text-white">AVAILABLE</span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="font-headline text-xl font-bold text-foreground mb-1 group-hover:text-accent transition-colors duration-300">
                  {member.name}
                </h3>

                <p className="text-sm font-cta text-accent mb-3">
                  {member.role}
                </p>

                <p className="text-sm text-technical-gray mb-4 leading-relaxed">
                  {member.specialty}
                </p>

                {/* Availability */}
                <div className="flex items-center space-x-2 mb-4 pb-4 border-b border-border">
                  <Icon name="ClockIcon" size={16} className="text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">{member.availability}</span>
                </div>

                {/* Languages */}
                <div className="flex flex-wrap gap-2">
                  {member.languages.map((lang, langIndex) =>
                <span
                  key={langIndex}
                  className="px-2 py-1 bg-muted text-xs text-technical-gray rounded">

                      {lang}
                    </span>
                )}
                </div>

                {/* Contact Button */}
                <button className="w-full mt-4 px-4 py-2 bg-accent/10 text-accent font-cta text-sm rounded-lg hover:bg-accent hover:text-primary transition-all duration-300 flex items-center justify-center space-x-2">
                  <Icon name="ChatBubbleLeftRightIcon" size={16} />
                  <span>CONTACT</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Team Stats */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
          { value: '50+', label: 'Years Combined Experience', icon: 'AcademicCapIcon' },
          { value: '1000+', label: 'Projects Supported', icon: 'RocketLaunchIcon' },
          { value: '24/7', label: 'Support Coverage', icon: 'ClockIcon' },
          { value: '98%', label: 'Client Satisfaction', icon: 'StarIcon' }].
          map((stat, index) =>
          <div key={index} className="text-center p-6 bg-card border border-border rounded-lg">
              <Icon name={stat.icon as any} size={32} className="text-accent mx-auto mb-3" />
              <div className="font-headline text-3xl font-bold text-foreground mb-2">
                {stat.value}
              </div>
              <div className="text-sm text-technical-gray">
                {stat.label}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

}